import {ALMOST_OUT_THRESHOLD, APPROACHING_THRESHOLD} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {
  type CopilotChatEntitlementQuotas,
  CopilotPlan,
  type QuotaLimitData,
  UserLevelBudgetState,
} from '@github-ui/copilot-chat-types'

import {isNotNullOrUndefined} from './copilot-chat-entitlement'

interface TbbStateInput {
  quotas: CopilotChatEntitlementQuotas | undefined
  plan: CopilotPlan | null
  hasCEorCBAccess: boolean
  isLicensedLimited: boolean
}

export interface TbbState {
  tokenBasedBillingEnabled: boolean
  overagesEnabled: boolean
  userLevelBudgetEnabled: boolean
  userLevelBudgetState: UserLevelBudgetState | null
  userLevelBudgetPercentRemaining: number | null
  quotaRemaining: number | undefined
  quotaExceeded: boolean
  quotaResetAt: string | undefined | null
  overagesRemaining: number | undefined
  overagesExceeded: boolean
  overagesBudgetAmount: number | null
  overagesBudgetSpend: number | null
  tbbQuotaExceeded: boolean
  isIndividualPaidOrStudentPlan: boolean
  isCommercialPlan: boolean
  quotaTotalAmount: number | null
  quotaUsedAmount: number | null
  creditsUsed: number | null
}

// Gross AI Credits used this month is optional and comes straight from JSON with
// no runtime schema, so guard against non-numeric/negative/non-finite values.
function normalizeCreditsUsed(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

function getUserLevelBudgetState(
  userLevelBudgetEnabled: boolean,
  piQuota: QuotaLimitData | undefined,
): UserLevelBudgetState | null {
  if (!userLevelBudgetEnabled || !piQuota) return null
  const unlimited = !!piQuota.unlimited
  const hasQuota = piQuota.hasQuota !== false
  // Order matters: usage-blocked and no-limit both have unlimited truthy.
  if (unlimited && !hasQuota) return UserLevelBudgetState.UsageBlocked
  if (unlimited) return UserLevelBudgetState.NoLimit
  if (piQuota.percentRemaining <= 0) return UserLevelBudgetState.BudgetReached
  if (piQuota.percentRemaining <= ALMOST_OUT_THRESHOLD) return UserLevelBudgetState.BudgetAlmostReached
  if (piQuota.percentRemaining <= APPROACHING_THRESHOLD) return UserLevelBudgetState.BudgetProgress
  return null
}

function getTbbQuotaExceeded(
  plan: CopilotPlan | null,
  piQuota: QuotaLimitData | undefined,
  quotaExceeded: boolean,
  overagesEnabled: boolean,
  overagesExceeded: boolean,
  tokenBasedBillingEnabled: boolean,
): boolean {
  if (!tokenBasedBillingEnabled) return false
  if (plan === CopilotPlan.Business || plan === CopilotPlan.Enterprise) {
    return !!piQuota && !piQuota.unlimited && piQuota.percentRemaining <= 0
  }
  const quotaExceededButOveragesNotAllowed = quotaExceeded && !overagesEnabled
  const overagesQuotaExceeded = overagesEnabled && quotaExceeded && overagesExceeded
  return quotaExceededButOveragesNotAllowed || overagesQuotaExceeded
}

// Selects the appropriate quota source based on user type:
// - Free/limited users → chatQuota
// - Paid individual users → premiumInteractionsQuota
function getQuotaForUser(
  quotas: CopilotChatEntitlementQuotas | undefined,
  isLicensedLimited: boolean,
): QuotaLimitData | undefined {
  if (isLicensedLimited) {
    return quotas?.chatQuota
  }
  return quotas?.premiumInteractionsQuota
}

export function getTbbEntitlement({quotas, plan, hasCEorCBAccess, isLicensedLimited}: TbbStateInput): TbbState {
  const tokenBasedBillingEnabled = !!quotas?.tokenBasedBillingEnabled

  const overagesEnabled =
    (tokenBasedBillingEnabled ? quotas?.overageQuota?.permitted : quotas?.overagesEnabled) ?? false

  const userLevelBudgetEnabled = tokenBasedBillingEnabled && hasCEorCBAccess

  const piQuota = quotas?.premiumInteractionsQuota
  const userLevelBudgetState = getUserLevelBudgetState(userLevelBudgetEnabled, piQuota)
  const userLevelBudgetPercentRemaining = userLevelBudgetEnabled && piQuota ? piQuota.percentRemaining : null

  // TBB Quota: free users use chatQuota, paid individual users use premiumInteractionsQuota
  const userQuota = getQuotaForUser(quotas, isLicensedLimited)
  const quotaRemaining = userQuota?.percentRemaining
  const quotaExceeded = isNotNullOrUndefined(quotaRemaining) && quotaRemaining <= 0
  const quotaTotalAmount = userQuota?.total ?? null
  const quotaUsedAmount = userQuota?.used ?? null
  // Gross AI Credits used only applies to CE/CB users on token-based billing.
  const creditsUsed = userLevelBudgetEnabled ? normalizeCreditsUsed(piQuota?.creditsUsed) : null
  const quotaResetAt = quotas?.resetDateUtc ?? quotas?.resetDate
  const overagesRemaining = quotas?.overageQuota?.percentRemaining
  const overagesExceeded = isNotNullOrUndefined(overagesRemaining) && overagesRemaining <= 0
  const overagesBudgetAmount = quotas?.overageQuota?.budget ?? null
  const overagesBudgetSpend = quotas?.overageQuota?.spend ?? null

  const isIndividualPaidOrStudentPlan =
    plan === CopilotPlan.IndividualPro ||
    plan === CopilotPlan.IndividualStudent ||
    plan === CopilotPlan.IndividualProPlus ||
    plan === CopilotPlan.IndividualMax
  const isCommercialPlan = plan === CopilotPlan.Enterprise || plan === CopilotPlan.Business

  const tbbQuotaExceeded = getTbbQuotaExceeded(
    plan,
    piQuota,
    quotaExceeded,
    overagesEnabled,
    overagesExceeded,
    tokenBasedBillingEnabled,
  )

  return {
    tokenBasedBillingEnabled,
    overagesEnabled,
    userLevelBudgetEnabled,
    userLevelBudgetState,
    userLevelBudgetPercentRemaining,
    quotaRemaining,
    quotaExceeded,
    quotaTotalAmount,
    quotaUsedAmount,
    quotaResetAt,
    overagesRemaining,
    overagesExceeded,
    overagesBudgetAmount,
    overagesBudgetSpend,
    tbbQuotaExceeded,
    isIndividualPaidOrStudentPlan,
    isCommercialPlan,
    creditsUsed,
  }
}
